// client/src/coms/organisation/BookmarksView.js
// Personal bookmarks and collections (account-owned, server-side). Items the user can no longer access are
// shown as unavailable with no content. Collections: create, rename, delete, reorder (drag or keyboard), a
// default collection for new bookmarks, and bookmarks can belong to several collections at once.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { apiGetJson, apiPostJson, apiPatchJson, apiDelete, apiPut } from './orgApi';
import { formatDate, ProviderBadge, Dialog, useConfirm, usePrompt, Spinner, EmptyState, ErrorNote, notify, openTarget } from './orgShared';

const TYPE_LABELS = { message: 'Message', dm_message: 'Direct message', channel: 'Channel', dm: 'Conversation', server: 'Server', file: 'File', attachment: 'Attachment', external: 'Cloud file', canvas: 'Canvas', resource: 'Resource' };
const TYPE_ICONS = { message: '💬', dm_message: '✉️', channel: '#', dm: '👤', server: '🏠', file: '📄', attachment: '📎', external: '☁️', canvas: '🎨', resource: '📚' };

export default function BookmarksView() {
  const [collection, setCollection] = useState(0);
  const [q, setQ] = useState('');
  const [sort, setSort] = useState('date');
  const [type, setType] = useState('');
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [editing, setEditing] = useState(null);
  const [dragId, setDragId] = useState(null);
  const [confirm, confirmEl] = useConfirm();
  const [prompt, promptEl] = usePrompt();

  const seq = useRef(0);
  const load = useCallback(async () => {
    const mine = ++seq.current;
    try {
      const qs = new URLSearchParams({ sort: collection && sort === 'manual' ? 'manual' : sort, ...(collection ? { collection: String(collection) } : {}), ...(q ? { q } : {}), ...(type ? { type } : {}) });
      const r = await apiGetJson(`/me/bookmarks?${qs}`);
      if (mine !== seq.current) return; // superseded by a newer request (another collection or filter)
      setData(r);
      setErr(null);
    } catch (e) {
      if (mine === seq.current) setErr(e);
    }
  }, [collection, q, sort, type]);
  useEffect(() => {
    const t = setTimeout(load, q ? 250 : 0);
    return () => clearTimeout(t);
  }, [load, q]);
  useEffect(() => {
    const on = () => load();
    window.addEventListener('vox-bookmarks-changed', on);
    return () => window.removeEventListener('vox-bookmarks-changed', on);
  }, [load]);

  const cols = (data && data.collections) || [];

  async function newCollection() {
    const name = await prompt({ title: 'New collection', label: 'Name', confirmLabel: 'Create', placeholder: 'Read Later', maxLength: 60 });
    if (!name) return;
    try {
      const r = await apiPostJson('/me/bookmark-collections', { name });
      setCollection(r.collection.id);
    } catch (e) {
      notify(e.message, { tone: 'error' });
    }
  }
  async function renameCollection(c) {
    const name = await prompt({ title: 'Rename collection', label: 'Name', value: c.name, maxLength: 60 });
    if (!name || name === c.name) return;
    await apiPatchJson(`/me/bookmark-collections/${c.id}`, { name }).catch((e) => notify(e.message, { tone: 'error' }));
    load();
  }
  async function deleteCollection(c) {
    if (!(await confirm({ title: `Delete “${c.name}”?`, body: 'The collection is removed; its bookmarks stay in All bookmarks.', confirmLabel: 'Delete collection', danger: true }))) return;
    await apiDelete(`/me/bookmark-collections/${c.id}`).catch(() => {});
    setCollection(0);
    load();
  }
  async function toggleDefault(c) {
    await apiPatchJson(`/me/bookmark-collections/${c.id}`, { is_default: !c.is_default }).catch(() => {});
    notify(c.is_default ? 'No default collection' : `New bookmarks go to “${c.name}”`, { tone: 'success' });
    load();
  }
  async function moveCollection(c, delta) {
    const ids = cols.map((x) => x.id);
    const i = ids.indexOf(c.id);
    const j = i + delta;
    if (j < 0 || j >= ids.length) return;
    [ids[i], ids[j]] = [ids[j], ids[i]];
    await apiPut('/me/bookmark-collections/order', { ids }).catch(() => {});
    load();
  }
  async function remove(b) {
    await apiDelete(`/me/bookmarks/${b.id}`).catch(() => {});
    notify('Bookmark removed', {
      action: { label: 'Undo', onClick: () => apiPostJson('/me/bookmarks', { target_type: b.target_type, target_key: b.target_key, note: b.note || undefined, collection_ids: b.collection_ids }).then(load, () => notify('That item is no longer available', { tone: 'error' })) },
    });
    load();
  }
  async function reorderItems(fromId, toId) {
    if (!collection || fromId === toId) return;
    const ids = data.bookmarks.map((b) => b.id);
    const from = ids.indexOf(fromId);
    const to = ids.indexOf(toId);
    if (from < 0 || to < 0) return;
    ids.splice(to, 0, ids.splice(from, 1)[0]);
    await apiPatchJson(`/me/bookmark-collections/${collection}`, { item_order: ids }).catch(() => {});
    setSort('manual');
    load();
  }

  return (
    <div className="coms-view vxo-view vx-pz" data-testid="bookmarks-view">
      <header className="vxo-head">
        <div className="vxo-head-title">
          <h1>Bookmarks</h1>
          <span className="vxo-head-sub">Private to your account</span>
        </div>
      </header>
      <div className="vxo-split">
        <nav className="vxo-nav" aria-label="Bookmark collections">
          <button type="button" className={`vxo-nav-item${!collection ? ' is-active' : ''}`} onClick={() => setCollection(0)} data-testid="bookmarks-all">
            <span className="vxo-nav-icon" aria-hidden="true">
              🔖
            </span>
            <span className="vxo-nav-text">
              <span className="vxo-nav-label">All bookmarks</span>
              {data ? <span className="vxo-nav-sub">{data.total}</span> : null}
            </span>
          </button>
          {cols.map((c) => (
            <div key={c.id} className="vxo-nav-row">
              <button type="button" className={`vxo-nav-item${collection === c.id ? ' is-active' : ''}`} onClick={() => setCollection(c.id)} data-testid="bookmark-collection">
                <span className="vxo-nav-icon" aria-hidden="true">
                  {c.is_default ? '⭐' : '🗂️'}
                </span>
                <span className="vxo-nav-text">
                  <span className="vxo-nav-label">{c.name}</span>
                  <span className="vxo-nav-sub">
                    {c.count}
                    {c.is_default ? ' · default' : ''}
                  </span>
                </span>
              </button>
              {collection === c.id ? (
                <span className="vxo-nav-tools">
                  <button type="button" className="vx-icon-btn" aria-label="Move up" onClick={() => moveCollection(c, -1)}>
                    ↑
                  </button>
                  <button type="button" className="vx-icon-btn" aria-label="Move down" onClick={() => moveCollection(c, 1)}>
                    ↓
                  </button>
                  <button type="button" className="vx-icon-btn" aria-label={c.is_default ? 'Stop using as default' : 'Use as default for new bookmarks'} title={c.is_default ? 'Default for new bookmarks' : 'Make default'} onClick={() => toggleDefault(c)}>
                    ⭐
                  </button>
                  <button type="button" className="vx-icon-btn" aria-label={`Rename ${c.name}`} onClick={() => renameCollection(c)}>
                    ✎
                  </button>
                  <button type="button" className="vx-icon-btn" aria-label={`Delete ${c.name}`} onClick={() => deleteCollection(c)}>
                    🗑
                  </button>
                </span>
              ) : null}
            </div>
          ))}
          <button type="button" className="vx-btn vxo-connect-btn" onClick={newCollection} data-testid="bookmark-new-collection">
            ＋ New collection
          </button>
        </nav>
        <section className="vxo-main">
          <div className="vxo-toolbar">
            <div className="vxo-toolbar-actions">
              <input className="vxo-input" type="search" placeholder="Search bookmarks and notes" aria-label="Search bookmarks" value={q} onChange={(e) => setQ(e.target.value)} data-testid="bookmarks-search" />
              <select className="vxo-select" aria-label="Type" value={type} onChange={(e) => setType(e.target.value)}>
                <option value="">All types</option>
                {Object.entries(TYPE_LABELS).map(([k, v]) => (
                  <option key={k} value={k}>
                    {v}
                  </option>
                ))}
              </select>
              <select className="vxo-select" aria-label="Sort" value={sort} onChange={(e) => setSort(e.target.value)}>
                <option value="date">Newest first</option>
                <option value="name">Name</option>
                <option value="type">Type</option>
                {collection ? <option value="manual">My order</option> : null}
              </select>
            </div>
          </div>
          <ErrorNote error={err} onRetry={load} />
          {!data && !err ? <Spinner /> : null}
          {data && !data.bookmarks.length ? (
            <EmptyState icon="🔖" title={q || type ? 'No bookmarks match' : collection ? 'This collection is empty' : 'No bookmarks yet'}>
              Use “Save bookmark” on messages, channels, files, resources and servers to keep them here.
            </EmptyState>
          ) : null}
          <ul className="vxo-result-list">
            {(data ? data.bookmarks : []).map((b) => {
              const t = b.target || {};
              return (
                <li
                  key={b.id}
                  className={`vxo-result${t.available ? '' : ' is-unavailable'}`}
                  draggable={!!collection}
                  onDragStart={() => setDragId(b.id)}
                  onDragOver={(e) => collection && e.preventDefault()}
                  onDrop={() => reorderItems(dragId, b.id)}
                  data-testid="bookmark-item"
                >
                  <button type="button" className="vxo-result-main" disabled={!t.available} onClick={() => openTarget(t.open)}>
                    <span className="vxo-result-icon" aria-hidden="true">
                      {TYPE_ICONS[b.target_type]}
                    </span>
                    <span className="vxo-result-body">
                      <span className="vxo-result-title">
                        {t.provider ? <ProviderBadge provider={t.provider} /> : null}
                        {t.available ? t.title : t.reason === 'disconnected' ? 'Storage disconnected' : 'Unavailable'}
                        {t.archived ? <span className="vxo-badge">archived</span> : null}
                        <span className="vxo-badge">{TYPE_LABELS[b.target_type]}</span>
                      </span>
                      {t.available && t.subtitle ? <span className="vxo-result-snippet">{t.subtitle}</span> : null}
                      {!t.available ? <span className="vxo-result-snippet">{t.reason === 'disconnected' ? 'The library this file was in has been disconnected.' : 'You no longer have access to this item, or it was deleted.'}</span> : null}
                      {b.note ? <span className="vxo-note">📝 {b.note}</span> : null}
                      <span className="vxo-result-meta">
                        {[t.server && t.server.name, t.author ? `by ${t.author}` : null, `saved ${formatDate(b.created_at)}`].filter(Boolean).join(' · ')}
                      </span>
                    </span>
                  </button>
                  <button type="button" className="vx-icon-btn" aria-label="Edit note and collections" title="Note & collections" onClick={() => setEditing(b)}>
                    ✎
                  </button>
                  <button type="button" className="vx-icon-btn" aria-label="Remove bookmark" title="Remove" onClick={() => remove(b)}>
                    ✕
                  </button>
                </li>
              );
            })}
          </ul>
        </section>
      </div>
      {editing ? <BookmarkEditor b={editing} collections={cols} onClose={() => setEditing(null)} onSaved={() => {
        setEditing(null);
        load();
      }} /> : null}
      {confirmEl}
      {promptEl}
    </div>
  );
}

function BookmarkEditor({ b, collections, onClose, onSaved }) {
  const [note, setNote] = useState(b.note || '');
  const [ids, setIds] = useState(b.collection_ids || []);
  return (
    <Dialog
      title="Bookmark"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="vx-btn" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="vx-btn primary"
            onClick={async () => {
              try {
                await apiPatchJson(`/me/bookmarks/${b.id}`, { note, collection_ids: ids });
                onSaved();
              } catch (e) {
                notify(e.message, { tone: 'error' });
              }
            }}
            data-testid="bookmark-save"
          >
            Save
          </button>
        </>
      }
    >
      <label className="vxo-field">
        <span>Note (only you can see it)</span>
        <textarea rows={3} maxLength={1000} value={note} onChange={(e) => setNote(e.target.value)} />
      </label>
      <fieldset className="vxo-fieldset">
        <legend>Collections</legend>
        {!collections.length ? <p className="vxo-muted">Create a collection first.</p> : null}
        <div className="vxo-checks">
          {collections.map((c) => (
            <label key={c.id} className="vxo-check">
              <input type="checkbox" checked={ids.includes(c.id)} onChange={(e) => setIds(e.target.checked ? [...ids, c.id] : ids.filter((x) => x !== c.id))} />
              <span>{c.name}</span>
            </label>
          ))}
        </div>
      </fieldset>
    </Dialog>
  );
}
